// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  AssetBrand,
  ChartContainer,
  WatchlistToggleButton,
} from '@shared/ui-kit/components';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

export interface TopAssetsProps {
  assets: Asset[];
}

interface Asset {
  ticker: string;
  weight: number;
  name: string;
  outstandingBalance?: number;
  logo: string;
  url: string;
  assetType: string;
}

export const TopAssets: FC<TopAssetsProps> = ({ assets }) => {
  const { t } = useTranslation('common');
  return (
    <ChartContainer
      header={t('common.top-assets')}
      popupName="charts.topAssets"
      tooltip
    >
      <table className="w-full self-start font-poppins text-primary-medium">
        <thead>
          <tr className="[&>th]:pb-4 [&>th]:font-normal">
            <th>{t('common.name')}</th>
            <th className="w-24">{t('common.total-percentage')}</th>
            <th className="w-24 text-right">{t('common.investment')}</th>
          </tr>
        </thead>
        <tbody>
          {assets.map(asset => {
            return (
              <tr key={asset.ticker} className="[&>td]:py-3">
                <td>
                  <AssetBrand
                    ticker={asset.ticker}
                    assetType={asset.assetType}
                    logoUrl={asset.logo}
                    url={asset.url}
                    label={asset.name}
                    className="w-fit"
                    watchlistButton={
                      <WatchlistToggleButton
                        ticker={asset.ticker}
                        assetType={asset.assetType}
                      />
                    }
                  />
                </td>
                <td className="!money-display w-24 pl-2">
                  {(asset.weight * 100).toFixed(1)}%
                </td>
                <td className="!money-display w-32 pl-2 text-right">
                  {formatToMoney(asset.outstandingBalance as number)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ChartContainer>
  );
};
